import {getCommandKey, getModifierLabel, KeyModifiers} from 'common/util/keyboard';

/** Stands in for the platform's primary modifier: ⌘ on macOS, Ctrl elsewhere. */
export const PlatformCommandKey = `platform-command`;

export type ShortcutModifier = KeyModifiers | typeof PlatformCommandKey;

/**
 * A keyboard shortcut declared once by whoever owns the behavior, so the key
 * binding and any UI that labels it (the command palette, menus) can't drift.
 */
export interface KeyboardShortcut {
  /** `KeyboardEvent.code` the binding matches, e.g. `KeyZ`. */
  code: string;
  /** Modifiers held with `code`, in display order. */
  modifiers?: ShortcutModifier[];
  /** Key cap label for `code`. Defaults to the code minus its `Key`/`Digit` prefix. */
  keyLabel?: string;
}

function resolveModifier(modifier: ShortcutModifier): KeyModifiers {
  return modifier === PlatformCommandKey ? getCommandKey() : modifier;
}

/** The shortcut as a `keyEventSwitcher` handler-map key. */
export function getShortcutBinding({code, modifiers = []}: KeyboardShortcut): string {
  return [...modifiers.map(resolveModifier), code].join(` + `);
}

/** The shortcut as one key cap label per key, in display order. */
export function getShortcutKeyCaps({code, modifiers = [], keyLabel}: KeyboardShortcut): string[] {
  const modifierLabels = modifiers.map((modifier) => getModifierLabel(resolveModifier(modifier)));
  return [...modifierLabels, keyLabel ?? code.replace(/^(Key|Digit)/, ``)];
}
